import type { Store } from "@hobbyshopify/backend-client";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { claimShopPage, updateStore } from "../../core/ops/store-ops";
import { errorText } from "../../core/primitives/errors";
import { hasErrors } from "../../core/primitives/form-errors";
import {
  contactEmailError,
  hasMappableAddress,
  hostsShopPage,
  PRESET_DESCRIPTION,
  returnsPolicyError,
} from "../../core/primitives/shop-page";
import { shopSlugError, suggestShopSlug } from "../../core/primitives/shop-slug";
import {
  type HostedErrors,
  type HostedField,
  OnboardingWebsiteActions,
  OnboardingWebsiteState,
  type WebsiteChoice,
} from "./onboarding-website-model";

export type OnboardingWebsiteDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "locationsRepository">;

export interface OnboardingWebsiteParams {
  claimOnboarding: boolean;
  storeIdParam: string | null;
}

export interface OnboardingWebsiteRouting {
  toPrevious(storeId: string, claimWalkthrough: boolean): void;
  toPayments(storeId: string, claimWalkthrough: boolean): void;
}

/** Typing "cardcorner.com" is the common case and is not a URL until it has a scheme; the backend requires one. */
export const withScheme = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/**
 * Step 3: where customers, and Stripe, can find this shop online. Required, not
 * skippable: Stripe checks every account's web address, and there is a working
 * answer for every shop, which is what the hosted page is for. The hosted branch
 * refuses to claim a page `ShopPageOps.isPublishable` would reject.
 */
export class OnboardingWebsiteController extends SubtreeController implements OnboardingWebsiteActions {
  readonly state = new OnboardingWebsiteState();
  #seededFrom: Store | null | undefined;

  constructor(
    readonly deps: OnboardingWebsiteDeps,
    readonly params: OnboardingWebsiteParams,
    readonly routing: OnboardingWebsiteRouting,
  ) {
    super();
    this.subtree.put(OnboardingWebsiteState, this.state);
    this.subtree.put(OnboardingWebsiteActions, this);
    this.sync(() => {
      const store = deps.storeRepository.selectedStore;
      this.state.storeId.value = store?.id ?? null;
      this.state.claimWalkthrough.value = params.claimOnboarding && params.storeIdParam === (store?.id ?? null);
      // The shop can load after this screen mounts (a direct visit, a refresh),
      // and the suggested handle is derived from its name.
      if (store && store !== this.#seededFrom) this.#seed(store);
    }, [deps.storeRepository]);
    this.sync(() => {
      this.state.hasMappableLocation.value = hasMappableAddress(deps.locationsRepository.locations);
    }, [deps.locationsRepository]);
    this.sync(() => {
      this.state.slugProblem.value = this.state.choice.value === "hosted" ? shopSlugError(this.state.slug.value) : null;
    }, [this.state.choice, this.state.slug]);
  }

  setChoice = (choice: WebsiteChoice) => {
    this.state.choice.value = choice;
  };

  setWebsiteUrl = (value: string) => {
    this.state.websiteUrl.value = value;
  };

  setSlug = (value: string) => {
    this.state.slug.value = value.toLowerCase();
  };

  setContactEmail = (value: string) => {
    this.state.contactEmail.value = value;
    this.#clearHostedError("contactEmail");
  };

  setReturnsPolicy = (value: string) => {
    this.state.returnsPolicy.value = value;
    this.#clearHostedError("returnsPolicy");
  };

  submit = async () => {
    const store = this.deps.storeRepository.selectedStore;
    if (!store || this.state.busy.value) return;
    this.state.error.value = null;
    const claim = this.state.claimWalkthrough.value;

    if (this.state.choice.value === "own") {
      const trimmed = this.state.websiteUrl.value.trim();
      if (!trimmed) {
        this.state.error.value = "Enter your website address, or choose the second option.";
        return;
      }
      this.state.busy.value = true;
      const r = await updateStore({ ...this.deps, storeId: store.id, body: { websiteUrl: withScheme(trimmed) } });
      if (this.isDisposed) return;
      this.state.busy.value = false;
      if (!r.ok) {
        this.state.error.value = errorText(r.errors);
        return;
      }
      this.routing.toPayments(store.id, claim);
      return;
    }

    if (this.state.slugProblem.value) return;
    const contactEmail = this.state.contactEmail.value;
    const returnsPolicy = this.state.returnsPolicy.value;
    const problems: HostedErrors = {};
    const complain = (field: HostedField, message: string | null) => {
      if (message) problems[field] = message;
    };
    complain("contactEmail", contactEmailError(contactEmail));
    complain("returnsPolicy", returnsPolicyError(returnsPolicy));
    this.state.hostedErrors.value = problems;
    if (hasErrors(problems)) return;
    if (!this.state.hasMappableLocation.value) {
      this.state.error.value =
        "Add a street address to one of your locations first — the page needs one to be accepted.";
      return;
    }

    this.state.busy.value = true;
    // Claim first: it clears `websiteUrl` server-side, so the page content
    // written afterwards cannot be undone by it.
    const claimed = await claimShopPage({ ...this.deps, storeId: store.id, slug: this.state.slug.value });
    if (this.isDisposed) return;
    if (!claimed.ok) {
      this.state.busy.value = false;
      this.state.error.value = errorText(claimed.errors);
      return;
    }
    const updated = await updateStore({
      ...this.deps,
      storeId: store.id,
      body: {
        // Never overwrites what the merchant wrote on step 1; it only covers the
        // one shop that arrives here with nothing.
        description: store.description?.trim() || PRESET_DESCRIPTION,
        contactEmail: contactEmail.trim(),
        returnsPolicy: returnsPolicy.trim(),
      },
    });
    if (this.isDisposed) return;
    this.state.busy.value = false;
    if (!updated.ok) {
      this.state.error.value = errorText(updated.errors);
      return;
    }
    this.routing.toPayments(store.id, claim);
  };

  back = () => {
    this.routing.toPrevious(this.state.storeId.value ?? "", this.state.claimWalkthrough.value);
  };

  #seed(store: Store): void {
    this.#seededFrom = store;
    this.state.choice.value = hostsShopPage(store) ? "hosted" : "own";
    this.state.websiteUrl.value = store.websiteUrl ?? "";
    this.state.slug.value = store.slug ?? suggestShopSlug(store.name ?? "");
    this.state.contactEmail.value = store.contactEmail ?? "";
    this.state.returnsPolicy.value = store.returnsPolicy ?? "";
  }

  /** Typing into a field the merchant was just told about clears the complaint. */
  #clearHostedError(field: HostedField): void {
    if (this.state.hostedErrors.value[field]) this.state.hostedErrors.update(({ [field]: _, ...rest }) => rest);
  }
}
